"use client";

import { useCallback } from 'react';
import { useI18n } from '@/providers/i18n';
import { pickLocalized } from '@/lib/i18n/pick-localized';
import { useSubscription } from '@/providers/subscription';
import type { TersaModel } from '@/lib/providers';

type LocalizableModel = Pick<TersaModel, 'id' | 'label' | 'labelI18n' | 'note' | 'noteI18n'> & {
  // model_access 覆盖元数据（由 providers/subscription.tsx 注入到模型上）
  category?: string | null;
  categoryI18n?: Record<string, string> | null;
  // 展示徽标（如 4K / HD），仅来自 model_access.badges（无前端硬编码兜底）
  badgeI18n?: Record<string, string> | null;
};

/**
 * 根据当前 i18n 语言解析模型的本地化名称 / 备注 / 分类。
 *
 * 展示名已迁移到数据库（model_access.labels）：`filterModelsClient` 会把数据库
 * 的 labels 注入到 m.labelI18n 上。数据库未配置时回退到模型 id（m.id）。
 */
export function useModelLabel() {
  const { i18n } = useI18n();
  const language = i18n.language;

  const getLabel = useCallback(
    (m: LocalizableModel | undefined | null): string => {
      if (!m) return '';
      // 数据库 labels（注入到 labelI18n）→ 历史硬编码 label → 模型 id 兜底
      const picked = pickLocalized(m.label ?? '', m.labelI18n, language);
      return picked.length > 0 ? picked : (m.id ?? '');
    },
    [language],
  );

  const getNote = useCallback(
    (m: LocalizableModel | undefined | null): string | null => {
      if (!m) return null;
      const picked = pickLocalized(m.note ?? '', m.noteI18n ?? undefined, language);
      return picked.length > 0 ? picked : null;
    },
    [language],
  );

  const getCategory = useCallback(
    (m: LocalizableModel | undefined | null): string | null => {
      if (!m) return null;
      const picked = pickLocalized(m.category ?? '', m.categoryI18n ?? undefined, language);
      return picked.length > 0 ? picked : null;
    },
    [language],
  );

  // 徽标支持多个：后台以 `|`（或全角 ｜）分隔，如 "真人|4K|首尾帧"。
  const getBadges = useCallback(
    (m: LocalizableModel | undefined | null): string[] => {
      if (!m) return [];
      const picked = pickLocalized('', m.badgeI18n ?? undefined, language);
      if (!picked) return [];
      return picked.split(/[|｜]/).map((s) => s.trim()).filter(Boolean);
    },
    [language],
  );

  return { getLabel, getNote, getCategory, getBadges, language };
}

/**
 * 仅给定 modelId 时，按当前 locale 解析数据库展示名。
 *
 * 命中 model_access.labels 时返回本地化名；未配置时返回 undefined，
 * 由调用方决定兜底（各调用点均已回退到 modelId）。
 *
 * 用于无法访问完整 model 对象的场景（例如 history 卡片只持有 modelId）。
 */
export function useModelLabelById() {
  const { i18n } = useI18n();
  const language = i18n.language;
  const { modelAccessMap } = useSubscription();

  return useCallback(
    (modelId: string | undefined | null): string | undefined => {
      if (!modelId) return undefined;
      const labels = modelAccessMap?.[modelId]?.labels;
      if (!labels) return undefined;
      return pickLocalized('', labels, language) || undefined;
    },
    [modelAccessMap, language],
  );
}
